import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import PayrollHeroAnimatedText from './PayrollHeroAnimatedText';
import Breadcrumbs from '../navigation/Breadcrumbs';
import { buildServiceBreadcrumbs } from '../navigation/breadcrumbUtils';
import { getBreadcrumbSchema } from '../navigation/breadcrumbSchema';
import SEO from '../seo/SEO';
import './PayrollProcessing.css';

const PayrollProcessing = () => {
  const location = useLocation();
  const servicesIndexView = location.state?.servicesIndexView;
  const backPath =
    servicesIndexView === 'payroll' ||
    location.pathname.startsWith('/services/payroll/')
      ? '/services/payroll'
      : servicesIndexView
        ? '/services'
        : '/';

  const breadcrumbItems = buildServiceBreadcrumbs('payroll', 'Payroll Processing');
  const breadcrumbSchema = getBreadcrumbSchema(breadcrumbItems);

  return (
    <div className="standard-page-wrapper">
      <SEO schema={breadcrumbSchema} />
      <div className="standard-page-container">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="standard-back-link">
          <Link
            to={backPath}
            state={servicesIndexView ? { servicesIndexView } : undefined}
          >
            {servicesIndexView ? '\u2190 Back' : '\u2190 Back to Home'}
          </Link>
        </div>

        <div className="standard-page-hero">
          <PayrollHeroAnimatedText
            as="h1"
            text="Payroll Processing Services"
            className="payroll-hero-title"
            variant="arc-in"
            threshold={0}
            rootMargin="0px"
            step={56}
          />
          <p>
            BlackRoth provides accurate, secure, and compliant Payroll
            Processing Services that ensure timely salary execution,
            statutory adherence, and seamless payroll operations.
          </p>
        </div>

        <section className="standard-page-section">
          <h2>Overview</h2>
          <p>
            Payroll processing is a critical operational function that requires
            precision, confidentiality, and regulatory compliance. BlackRoth
            manages the complete payroll lifecycle, allowing organizations
            to focus on core business activities while ensuring payroll
            accuracy and reliability.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>Core Capabilities</h2>
          <p>
            Our payroll processing solutions are designed to handle complex
            compensation structures, multi-location employees, and varying
            statutory requirements with consistency and control.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>Payroll Processing Services We Offer</h2>
          <ul className="standard-service-list">
            <li>Monthly & Bi-Weekly Payroll Processing</li>
            <li>Salary Calculation & Net Pay Validation</li>
            <li>Statutory Deductions & Tax Computation</li>
            <li>Payslip Generation & Distribution</li>
            <li>Reimbursements & Allowance Processing</li>
            <li>Attendance & Leave Data Processing</li>
            <li>Payroll Reports & Audit Support</li>
            <li>Error Handling & Payroll Reconciliation</li>
          </ul>
        </section>

        <section className="standard-page-section">
          <h2>Business Value</h2>
          <p>
            BlackRoth's Payroll Processing Services reduce administrative
            overhead, eliminate payroll errors, ensure compliance, and
            improve employee satisfaction through timely and transparent
            salary management.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>Technology & Integration</h2>
          <p>
            Our payroll processing workflows integrate seamlessly with HRMS,
            attendance systems, accounting platforms, and banking interfaces
            using secure, cloud-based and API-driven architectures.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>Security & Compliance</h2>
          <p>
            BlackRoth ensures strict payroll data security through encryption,
            role-based access control, audit logs, and adherence to statutory
            and data protection regulations.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PayrollProcessing;
